/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-2023 (original work) Open Assessment Technologies SA ;
 */

/**
 * @typedef {Object} Theme
 * @property {String} id - the theme identifier (used in data-theme)
 * @property {String} [title] - metadata
 * @property {String} [stylesheet] - URL to the stylesheet to load
 * @property {Boolean} [active] - is the theme active
 */

/**
 * Helps you to handle theme loading and toggling
 * @param {Theme[]} themes - the list of themes to handle
 * @param {Object} [options]
 * @param {Boolean} [options.killSwitch] - disable the stylesheet when toggling the theme
 * @param {HTMLElement} [options.stylesheetContainer] - where stylesheets get appended
 * @param {HTMLElement} [options.themeScope] - the element that contains the data-theme attribute
 * @returns {Object} the handler
 */
export default function themeHandlerFactory(
    themes = [],
    {
        killSwitch = true,
        stylesheetContainer = document.head,
        themeScope = document.body
    } = {}
) {
    return {

        /**
         * Load the themes, if one has a stylesheet to load, it's loaded
         * @param {Boolean} [activate] - activate right now the active or default theme
         * @returns {Promise} once loaded
         */
        load(activate = true) {
            return Promise.all(
                themes.map(
                    theme => (
                        new Promise((resolve, reject) => {
                            if (
                                theme.stylesheet &&
                                typeof theme.stylesheet === 'string' &&
                                !stylesheetContainer.querySelector(`link[data-theme-id='${theme.id}]`)
                            ) {
                                const link = document.createElement('link');
                                link.type = 'text/css';
                                link.rel = 'stylesheet';
                                link.disabled = killSwitch;
                                link.dataset.themeId = theme.id;
                                link.href = theme.stylesheet;

                                link.addEventListener('load', resolve);
                                link.addEventListener('error', reject);

                                stylesheetContainer.appendChild(link);
                            } else {
                                resolve();
                            }
                        })
                    )
                )
            ).then(() => {
                if (activate) {
                    //find by property active
                    let activeTheme = this.getActive();

                    //or a default theme
                    if (!activeTheme) {
                        activeTheme = themes.find(theme => theme.id === 'default');
                    }

                    //or take the first
                    if (!activeTheme) {
                        activeTheme = themes[0];
                    }
                    if(activeTheme){
                        this.activate(activeTheme.id);
                    }
                }
            });
        },

        /**
         * Activate a theme
         * @param {String} themeId - the id of the theme to activate
         * @throws {Error} if the id is wrong
         */
        activate(themeId) {
            const matchById = theme => theme.id === themeId;
            if (!themeId || !themes.find(matchById)) {
                throw new Error(`Invalid or missing theme ${themeId}`);
            }

            themes.forEach(theme => (theme.active = themeId === theme.id));

            if (killSwitch) {
                const stylesheets = stylesheetContainer.querySelectorAll('link[data-theme-id]');
                for (let stylesheet of stylesheets) {
                    stylesheet.disabled = stylesheet.dataset.themeId !== themeId;
                }
            }
            if (themeScope && themeScope.dataset) {
                themeScope.dataset.theme = themeId;
            }
        },

        /**
         * Get the active theme
         * @returns {Theme} the active theme
         */
        getActive() {
            return themes.find(theme => theme.active);
        },

        /**
         * Destroy themeHandler and clean up its effects
         */
        destroy() {
            const stylesheets = stylesheetContainer.querySelectorAll('link[data-theme-id]');
            for (let stylesheet of stylesheets) {
                stylesheet.remove();
            }
        }
    };
}
